import { PropsWithChildren } from 'react';
import { useCurrentStateAndParams } from '@uirouter/react';

import { AddonLifecycleStatus } from '@api/types.gen';

import { Alert } from '@@/Alert';
import { Widget, WidgetBody } from '@@/Widget';

import { useAddon } from '../queries/useAddon';

type Props = {
  isLoading: boolean;
  isError: boolean;
};

// Release may not exist during mid-operation; failed fetch is expected
const inProgressLabel: Partial<Record<AddonLifecycleStatus, string>> = {
  [AddonLifecycleStatus.ADDON_STATUS_INSTALLING]: 'installing',
  [AddonLifecycleStatus.ADDON_STATUS_UPGRADING]: 'upgrading',
  [AddonLifecycleStatus.ADDON_STATUS_UNINSTALLING]: 'uninstalling',
};

// Gates tabs dependent on Helm release. Shows error if missing; gentler message during operations.
export function AddonReleaseGuard({
  isLoading,
  isError,
  children,
}: PropsWithChildren<Props>) {
  const { params } = useCurrentStateAndParams();
  const addonQuery = useAddon(String(params.id ?? ''));
  const status = addonQuery.data?.lifecycleStatus;
  const progress = status ? inProgressLabel[status] : undefined;

  if (isError) {
    return (
      <div className="row">
        <div className="col-sm-12">
          <Widget>
            <WidgetBody>
              {progress ? (
                <Alert color="info" title={`Add-on is ${progress}`}>
                  This add-on is still {progress}. Its details will appear once
                  the operation completes.
                </Alert>
              ) : (
                <Alert color="error" title="Add-on data unavailable">
                  The add-on&apos;s Helm release could not be loaded. It may
                  have been removed outside Portainer, or the installation or
                  upgrade may have failed. Try reinstalling the add-on.
                </Alert>
              )}
            </WidgetBody>
          </Widget>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="row">
        <div className="col-sm-12">
          <Widget>
            <WidgetBody loading />
          </Widget>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
